import { Slide } from "@jelly/lesson";
import config from "appConfig";
import theory from "../../theory";

export const slide: Slide = {
  title: "",
  description:
    "This simple case study will show how different types of absorbers mounted on the walls of a rectangular room change its reverberation time. You can switch between different types of used absorbers, observe the change in reverberation time and listen to sound examples for speech and music.",
  theory: theory,
  layout: {
    type: "controlsToggleExtraContent",
    options: {
      controlsPosition: "bottom",
      controls: [
        {
          type: "dropdown",
          options: {},
          items: [
            { name: "Untreated empty room" },
            { name: "Room with MF absorbers" },
            { name: "Room optimized for speech" },
            { name: "Room with HF absorbers" },
            { name: "Room with LF absorbers" },
            { name: "Room optimized for music" },
          ],
        },
      ],
      extraContent: [
        [
          {
            type: "audioButton",
            options: {
              text: "Speech",
              theme: "primary",
              icon: "cis-volume-high",
              url: `${config.ASSETS_URL}/05/05/principle/untreated-empty-room-speech.wav`,
            },
          },
          {
            type: "audioButton",
            options: {
              text: "Music",
              theme: "secondary",
              icon: "cis-volume-high",
              url: `${config.ASSETS_URL}/05/05/principle/untreated-empty-room-music.wav`,
            },
          },
        ],
        [
          {
            type: "audioButton",
            options: {
              text: "Speech",
              theme: "primary",
              icon: "cis-volume-high",
              url: `${config.ASSETS_URL}/05/05/principle/room-with-mf-absorbers-speech.wav`,
            },
          },
          {
            type: "audioButton",
            options: {
              text: "Music",
              theme: "secondary",
              icon: "cis-volume-high",
              url: `${config.ASSETS_URL}/05/05/principle/room-with-mf-absorbers-music.wav`,
            },
          },
        ],
        [
          {
            type: "audioButton",
            options: {
              text: "Speech",
              theme: "primary",
              icon: "cis-volume-high",
              url: `${config.ASSETS_URL}/05/05/principle/room-optimized-for-speech-speech.wav`,
            },
          },
          {
            type: "audioButton",
            options: {
              text: "Music",
              theme: "secondary",
              icon: "cis-volume-high",
              url: `${config.ASSETS_URL}/05/05/principle/room-optimized-for-speech-music.wav`,
            },
          },
        ],
        [
          {
            type: "audioButton",
            options: {
              text: "Speech",
              theme: "primary",
              icon: "cis-volume-high",
              url: `${config.ASSETS_URL}/05/05/principle/room-with-hf-absorbers-speech.wav`,
            },
          },
          {
            type: "audioButton",
            options: {
              text: "Music",
              theme: "secondary",
              icon: "cis-volume-high",
              url: `${config.ASSETS_URL}/05/05/principle/room-with-hf-absorbers-music.wav`,
            },
          },
        ],
        [
          {
            type: "audioButton",
            options: {
              text: "Speech",
              theme: "primary",
              icon: "cis-volume-high",
              url: `${config.ASSETS_URL}/05/05/principle/room-with-lf-absorbers-speech.wav`,
            },
          },
          {
            type: "audioButton",
            options: {
              text: "Music",
              theme: "secondary",
              icon: "cis-volume-high",
              url: `${config.ASSETS_URL}/05/05/principle/room-with-lf-absorbers-music.wav`,
            },
          },
        ],
        [
          {
            type: "audioButton",
            options: {
              text: "Speech",
              theme: "primary",
              icon: "cis-volume-high",
              url: `${config.ASSETS_URL}/05/05/principle/room-optimized-for-music-speech.wav`,
            },
          },
          {
            type: "audioButton",
            options: {
              text: "Music",
              theme: "secondary",
              icon: "cis-volume-high",
              url: `${config.ASSETS_URL}/05/05/principle/room-optimized-for-music-music.wav`,
            },
          },
        ],
      ],
      components: [
        [
          {
            variant: "layout",
            data: {
              type: "table",
              options: {
                cols: 1,
                rows: 2,
                rowsSize: [4, 1],
                components: [
                  [
                    {
                      variant: "blockContainer",
                      data: {
                        block: {
                          type: "image",
                          options: {
                            src: `${config.ASSETS_URL}/05/05/principle/slide-2-untreated-empty-room.svg`,
                          },
                        },
                      },
                    },
                  ],
                  [
                    {
                      variant: "blockContainer",
                      data: {
                        block: {
                          type: "text",
                          options: {
                            data:
                              "The room is empty and untreated, only hard materials (plastered concrete and ceramic tiles) are used for the room interior. The reverberation time is very high and unsuitable for both speech or music. ",
                          },
                        },
                      },
                    },
                  ],
                ],
              },
            },
          },
        ],
        [
          {
            variant: "layout",
            data: {
              type: "table",
              options: {
                cols: 1,
                rows: 2,
                rowsSize: [4, 1],
                components: [
                  [
                    {
                      variant: "blockContainer",
                      data: {
                        block: {
                          type: "image",
                          options: {
                            src: `${config.ASSETS_URL}/05/05/principle/slide-2-room-with-mf-absorbers.svg`,
                          },
                        },
                      },
                    },
                  ],
                  [
                    {
                      variant: "blockContainer",
                      data: {
                        block: {
                          type: "text",
                          options: {
                            data:
                              "Two adjacent walls in the room are treated with medium-frequency (MF) absorbers in the form of perforated absorbers (20% perforation) with a 50 mm cavity filled with mineral wool. The reverberation time is quite irregular, very high at low and high frequencies. It is still unsuitable for both speech or music, the sound is both boomy and bright.",
                          },
                        },
                      },
                    },
                  ],
                ],
              },
            },
          },
        ],
        [
          {
            variant: "layout",
            data: {
              type: "table",
              options: {
                cols: 1,
                rows: 2,
                rowsSize: [4, 1],
                components: [
                  [
                    {
                      variant: "blockContainer",
                      data: {
                        block: {
                          type: "image",
                          options: {
                            src: `${config.ASSETS_URL}/05/05/principle/slide-2-room-optimized-for-speech.svg`,
                          },
                        },
                      },
                    },
                  ],
                  [
                    {
                      variant: "blockContainer",
                      data: {
                        block: {
                          type: "text",
                          options: {
                            data:
                              "The room is optimized for speech and lectures. The walls are covered with tuned low-frequency (LF), medium-frequency (MF) and high-frequency (HF) absorbers. The reverberation time is very even throughout the whole frequency range and adjusted to speech. For music, the room is too damped.",
                          },
                        },
                      },
                    },
                  ],
                ],
              },
            },
          },
        ],
        [
          {
            variant: "layout",
            data: {
              type: "table",
              options: {
                cols: 1,
                rows: 2,
                rowsSize: [4, 1],
                components: [
                  [
                    {
                      variant: "blockContainer",
                      data: {
                        block: {
                          type: "image",
                          options: {
                            src: `${config.ASSETS_URL}/05/05/principle/slide-2-room-with-hf-absorbers.svg`,
                          },
                        },
                      },
                    },
                  ],
                  [
                    {
                      variant: "blockContainer",
                      data: {
                        block: {
                          type: "text",
                          options: {
                            data:
                              "Two adjacent walls in the room are treated with high-frequency (HF) absorbers in the form of 25 mm thick mineral wool. The reverberation time is very irregular, still very high at low frequencies. It is still unsuitable for both speech or music, the sound is very boomy.",
                          },
                        },
                      },
                    },
                  ],
                ],
              },
            },
          },
        ],
        [
          {
            variant: "layout",
            data: {
              type: "table",
              options: {
                cols: 1,
                rows: 2,
                rowsSize: [4, 1],
                components: [
                  [
                    {
                      variant: "blockContainer",
                      data: {
                        block: {
                          type: "image",
                          options: {
                            src: `${config.ASSETS_URL}/05/05/principle/slide-2-room-with-lf-absorbers.svg`,
                          },
                        },
                      },
                    },
                  ],
                  [
                    {
                      variant: "blockContainer",
                      data: {
                        block: {
                          type: "text",
                          options: {
                            data:
                              "Two adjacent walls in the room are treated with low-frequency (LF) absorbers in the form of perforated panel (0.8% perforation) with a 200 mm air cavity filled with 100 mm mineral wool. The reverberation time is too high at high frequencies, which is still unsuitable for both speech or music, the sound is too bright.",
                          },
                        },
                      },
                    },
                  ],
                ],
              },
            },
          },
        ],
        [
          {
            variant: "layout",
            data: {
              type: "table",
              options: {
                cols: 1,
                rows: 2,
                rowsSize: [4, 1],
                components: [
                  [
                    {
                      variant: "blockContainer",
                      data: {
                        block: {
                          type: "image",
                          options: {
                            src: `${config.ASSETS_URL}/05/05/principle/slide-2-room-optimized-for-music.svg`,
                          },
                        },
                      },
                    },
                  ],
                  [
                    {
                      variant: "blockContainer",
                      data: {
                        block: {
                          type: "text",
                          options: {
                            data:
                              "Two adjacent walls in the room are treated with high-frequency (HF) absorbers in the form of 25 mm thick mineral wool. The reverberation time is very irregular, still very high at low frequencies. It is still unsuitable for both speech or music, the sound is very boomy.",
                          },
                        },
                      },
                    },
                  ],
                ],
              },
            },
          },
        ],
      ],
    },
  },
};
